import { camelToSnake } from '@eventbrite/string-utils';
import flow from 'lodash/fp/flow';
import fromPairs from 'lodash/fp/fromPairs';
import map from 'lodash/fp/map';
import toPairs from 'lodash/fp/toPairs';
import isString from 'lodash/isString';
import { emailDomainSuggestions } from '../constants';
/*
    Our current policy allows for space to make up the entirety of a password,
    or contain space characters at either end so when password is passed through
    we need to exempt it.
*/
const _getTrimmedValue = (value: string, key: string) =>
    isString(value) && key !== 'password' ? value.trim() : value;

/**
 * Converts the keys of formData from camel to snake case and trims the values
 * @param {*} formData
 */
export const parseFormPostData = (formData: {
    [x: string]: string | boolean | undefined;
}) =>
    flow([
        toPairs,
        map(([key, value]) => [
            camelToSnake(key),
            _getTrimmedValue(value, key),
        ]),
        fromPairs,
    ])(formData);

/*
        This functions calculates the amount of changes one has to do on a string to convert it to another string
        This in essense means it counts the amount of 'typos' in any given string compared with another
    */
export const getAmountOfTypos = ({
    emailDomain = '',
    domain = '',
}: {
    emailDomain: string;
    domain: string;
}) => {
    const grid: number[][] = [];
    for (let i = 0; i < emailDomain.length + 1; i++) {
        grid.push([i]);
        for (let j = 1; j < domain.length + 1; j++) {
            grid[i].push(j);
            if (i > 0 && j > 0) {
                if (emailDomain[i - 1] === domain[j - 1]) {
                    grid[i][j] = grid[i - 1][j - 1];
                } else {
                    grid[i][j] =
                        1 +
                        Math.min(
                            grid[i - 1][j - 1],
                            grid[i][j - 1],
                            grid[i - 1][j],
                        );
                }
            }
        }
    }

    return grid[emailDomain.length][domain.length];
};

export const getDomainSuggestions = (inputValue: string) => {
    // We split the email on '@'
    const splitedEmail = inputValue.split('@');

    // Validate if the email has atleast 1 character on both ends of the '@', that it has only 1 '@' and that it doesnt have '.com' written already
    const emailDomain =
        splitedEmail.length === 2 &&
        splitedEmail[1].length > 0 &&
        splitedEmail[0].length > 0 &&
        !splitedEmail[1].includes('.com')
            ? splitedEmail[1]
            : null;

    // Here we save the domains that include whatever the user has written so far
    const secondarySuggestions: string[] = [];

    // Here we save domains with possible typos, and the amount of possible typos
    const domainsWithTypos: any = [];

    // Here we save the domains that start with whatever the user has written so far
    const prioritySuggestions = emailDomainSuggestions.filter((domain) => {
        if (emailDomain && domain.startsWith(emailDomain)) {
            return true;
        } else if (emailDomain && domain.includes(emailDomain)) {
            secondarySuggestions.push(domain);
        } else if (emailDomain) {
            const possibleTypos = getAmountOfTypos({
                emailDomain,
                domain: domain.slice(0, emailDomain.length + 2), // We add 2 to the lenght to check possible future characters
            });

            // As a threshold 4 typos is the most that we tolerate to add to the possible domains array
            possibleTypos <= 4
                ? domainsWithTypos.push([domain, possibleTypos])
                : null;
        }
    });

    // We assmeble all the possible domains with the most probable ones first
    const allSuggestions = [...prioritySuggestions, ...secondarySuggestions];

    /*
        If prioritySuggestions is empty it means that the user has made a mistake
        So we check if the suggestions dropdown is not long enough
        And we add the possible domains with typos we stored
    */
    if (
        prioritySuggestions.length === 0 &&
        secondarySuggestions.length < 3 &&
        domainsWithTypos.length > 0
    ) {
        // We sort the domains with typos in an ascending order from less to more possible typos
        domainsWithTypos.sort((a: any, b: any) => a[1] - b[1]);

        // The limit for the dropdown is set to 3 in this instance
        while (allSuggestions.length < 3 && domainsWithTypos.length > 0) {
            allSuggestions.push(domainsWithTypos[0][0]);
            domainsWithTypos.shift();
        }
    }

    // We create the suggestions to show
    const emailDomainSuggestionDropdown = allSuggestions.map((domain) => ({
        content: splitedEmail[0] + '@' + domain + '.com',
        value: splitedEmail[0] + '@' + domain + '.com',
    }));

    return emailDomainSuggestionDropdown;
};
